import React, { useState, useCallback, useEffect } from 'react'
import { Row, Col, Form, Button } from 'react-bootstrap'
import { sortBy } from 'lodash';
import dayjs from 'dayjs';

import { ReportLookup, generateSimpleFilename } from '../../../pages/Reports/ReportHelpers';
import ApiServiceServerless from '../../../xhr_libs';
import MonthSelect from '../../MonthSelect2';
import SmallerLoader from '../../SmallerLoader';



export default function PlugCustomTemplateReports(props) {
  const { organizationId, organizationName, templates, meterGroups, showToast } = props;

  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [selectedMeterGroup, setSelectedMeterGroup] = useState(null)

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);

  const [isGeneratingReport, setIsGeneratingReport] = useState(false)

  const [payload, setPayload] = useState({});


  const setDefaultDates = useCallback(() => {
    const local_today = dayjs.utc(new Date());
    setEndDate(dayjs.utc(new Date(local_today.year(), local_today.month(), 1)));
    setStartDate(local_today.subtract(1, 'year'));
    setMinDate(dayjs.utc(new Date(2010, 0, 1)));
    setMaxDate(dayjs.utc(new Date(local_today.year() + 1, 11, 31)));
  }, []);

  useEffect(() => {
    if (!minDate) {
      setDefaultDates();
    }
  }, [setDefaultDates, minDate]);

  useEffect(() => {
    let payload = {
      custom_template_id: selectedTemplate,
      meter_group_id: selectedMeterGroup,
      start_year: dayjs.utc(startDate).year(),
      start_month: dayjs.utc(startDate).month() + 1,
      end_year: dayjs.utc(endDate).year(),
      end_month: dayjs.utc(endDate).month() + 1,
    }
    setPayload(payload)
  }, [selectedTemplate, selectedMeterGroup, startDate, endDate])



  const handleReportError = (error, showToast) => {
    let variant = 'danger';
    let title = 'Error';
    if (
      error.response &&
      error.response.status &&
      error.response.status === 501
    ) {
      variant = 'warning';
      title = 'Warning';
    }

    showToast(variant, title, error);
  };

  const handleSetReport = (url, fileName) => {
    const old_link = document.getElementById('report-download-link');
    if (old_link) {
      old_link.remove();
    }
    const link = document.createElement('a');
    link.href = url;
    link.id = 'report-download-link';
    link.target = '_blank';
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
  };

  const submitReport = () => {
    setIsGeneratingReport(true);

    const report = ReportLookup.find(
      (report) => report.type === 'excel_plug'
    ).reportInfo.find((report) => report.type === 'excel_plug');
    const fileName = generateSimpleFilename(organizationName, report);
    generateDelayedReport(report, fileName);

  };

  const callMakeFindReportRequest = (
    report,
    fileName,
    report_id,
    extension,
    attempt
  ) => {
    setTimeout(() => {
      makeFindReportRequest(report, fileName, report_id, extension, attempt);
    }, 3000);
  };

  const makeFindReportRequest = (
    report,
    fileName,
    report_id,
    extension,
    attempt
  ) => {
    if (attempt < 10) {
      ApiServiceServerless.get(`/reports/retrieve/${report_id}/${extension}`, {
        ...report['opts'],
        authorization_id: organizationId,
      })
        .then((res) => {
          if (res.data && res.data.byteLength && res.data.byteLength > 4) {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            handleSetReport(url, fileName);
            setIsGeneratingReport(false);
          } else {
            callMakeFindReportRequest(
              report,
              fileName,
              report_id,
              extension,
              attempt + 1
            );
          }
        })
        .catch((error) => {
          setIsGeneratingReport(false);
          handleReportError(error, showToast);
          throw error;
        });
    } else {
      showToast(
        'warning',
        'Warning',
        'This report is taking awhile to generate. A copy will be sent to you via email when it is completed'
      );
      setIsGeneratingReport(false);
    }
  };

  const generateDelayedReport = (report, fileName) => {
    ApiServiceServerless.post(
      `/reports/${report.type}`,
      { ...payload, organization_id: organizationId },
      {
        authorization_id: organizationId,
      }
    )
      .then((res) => {
        callMakeFindReportRequest(
          report,
          fileName,
          res.data['report_id'],
          res.data['extension'],
          0
        );
      })
      .catch((error) => {
        setIsGeneratingReport(false);
        handleReportError(error, showToast);
        throw error;
      });
  };


  return <div style={{ minHeight: '350px', width: '100%' }}><Row style={{ marginTop: "10px" }}>
    <Col md={3}></Col>
    <Col md={7}>
      <Form>
        <Row>
          <Form.Group as={Col}>
            <Form.Label>
              Template
              <span className='text-warning font-weight-bold pl-1'>
                *
              </span>
            </Form.Label>
            <Form.Control
              style={{ float: 'right' }}
              as='select'
              size='sm'
              value={selectedTemplate ? selectedTemplate : ''}
              onChange={(e) => setSelectedTemplate(e.target.value)}
            >
              <option hidden value={'null'} key={'null-opt'}>
                Select a template
              </option>
              {templates.length === 0 && (
                <option disabled value={'dis'} key={'null-dis'}>
                  No Templates
                </option>
              )}
              {sortBy(templates, ['name']).map((template) => (
                <option
                  key={`template-opt-${template.id}-${template.template_type}`}
                  value={template.id}
                >
                  {template.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col}>
            <Form.Label>
              Meter Group
              <span className='text-warning font-weight-bold pl-1'>
                *
              </span>
            </Form.Label>
            <Form.Control
              style={{ float: 'right' }}
              as='select'
              size='sm'
              value={selectedMeterGroup ? selectedMeterGroup : ''}
              onChange={(e) => setSelectedMeterGroup(e.target.value)}
            >
              <option hidden value={'null'} key={'null-opt'}>
                Select a meter Group
              </option>
              {meterGroups.length === 0 && (
                <option disabled value={'dis'} key={'null-dis'}>
                  No Meter Groups
                </option>
              )}
              {sortBy(meterGroups, ['name']).map((group) => (
                <option
                  key={`group-opt-${group.id}-${group.group_type}`}
                  value={group.id}
                >
                  {group.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Row>
        <Row>
          <Col style={{ padding: '0' }}>
            <MonthSelect
              label='Start Date'
              currentDate={startDate}
              setCurrentDate={setStartDate}
              min_date={minDate}
              max_date={maxDate}
              showPicker={true}
            />
          </Col><Col style={{ padding: '0' }}>
            <MonthSelect
              label='End Date'
              currentDate={endDate}
              setCurrentDate={setEndDate}
              min_date={minDate}
              max_date={maxDate}
              showPicker={true}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Button
              style={{ width: '100%' }}
              variant='primary'
              type='submit'
              disabled={
                !selectedTemplate ||
                !selectedMeterGroup ||
                isGeneratingReport
              }
              onClick={() => submitReport()}
            >
              {!isGeneratingReport && 'Generate Report'}
              {isGeneratingReport && <SmallerLoader text={'Generating Report'} />}
            </Button>
          </Col>
        </Row>
      </Form>

    </Col>
    <Col md={3}></Col>

  </Row></div>
}